import React, { useEffect, useState } from "react";
import { UserPlus, Trash2, Power, KeyRound, Loader2, X, AlertTriangle, Mail, MailWarning } from "lucide-react";
import { toast } from "sonner";
import AppLayout, { PageHeader } from "../components/AppLayout";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import GoogleAllowlistCard from "../components/GoogleAllowlistCard";

export default function UsersPage() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [mailStatus, setMailStatus] = useState(null); // {email, sent, error, action}
  const [confirmAction, setConfirmAction] = useState(null); // {kind: 'delete'|'reset', email}
  const [busyEmail, setBusyEmail] = useState(null);
  const isAdmin = me?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setItems(data.items || []);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Accès admin requis");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin]);

  const toggleActive = async (email, active) => {
    try {
      await api.patch(`/users/${encodeURIComponent(email)}/status`, { active });
      toast.success(active ? "Compte activé" : "Compte désactivé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const doReset = async (email) => {
    setBusyEmail(email);
    try {
      const { data } = await api.post(`/users/${encodeURIComponent(email)}/reset-password`);
      setMailStatus({ email, sent: data.email_sent, error: data.email_error, action: "reset" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusyEmail(null);
      setConfirmAction(null);
    }
  };

  const doDelete = async (email) => {
    setBusyEmail(email);
    try {
      await api.delete(`/users/${encodeURIComponent(email)}`);
      toast.success("Compte supprimé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusyEmail(null);
      setConfirmAction(null);
    }
  };

  if (!isAdmin) {
    return (
      <AppLayout>
        <PageHeader title="Utilisateurs" subtitle="Gestion des comptes Norea." />
        <div className="card-flat p-8 text-center">
          <AlertTriangle className="w-10 h-10 mx-auto text-status-incompletText mb-3" />
          <div className="font-heading text-xl text-norea-text">Accès réservé aux administrateurs</div>
          <div className="text-sm text-norea-textMuted mt-2">
            Contactez votre administrateur pour qu'il vous attribue ce rôle.
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageHeader
        title="Utilisateurs"
        subtitle="Créez et gérez les comptes des agents Norea."
        breadcrumb={[{ label: "Norea", to: "/" }, "Utilisateurs"]}
        actions={
          <button onClick={() => setOpenCreate(true)} className="btn-primary" data-testid="btn-new-user">
            <UserPlus className="w-4 h-4" />
            Nouveau compte
          </button>
        }
      />

      <div className="card-flat overflow-hidden">
        <table className="w-full" data-testid="users-table">
          <thead className="bg-norea-bg border-b border-norea-border">
            <tr className="text-left">
              <th className="px-4 py-3 overline">Nom</th>
              <th className="px-4 py-3 overline">Email (login)</th>
              <th className="px-4 py-3 overline">Rôle</th>
              <th className="px-4 py-3 overline">Statut</th>
              <th className="px-4 py-3 overline">Créé le</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="text-center text-sm text-norea-textMuted py-12">
                  Chargement…
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-sm text-norea-textMuted py-12">
                  Aucun utilisateur
                </td>
              </tr>
            )}
            {items.map((u) => (
              <tr
                key={u.email}
                className={`border-b border-norea-border ${u.active === false ? "opacity-50" : "hover:bg-norea-surfaceHover"} transition`}
                data-testid={`user-row-${u.email}`}
              >
                <td className="px-4 py-3 text-sm font-medium text-norea-text">{u.name || "—"}</td>
                <td className="px-4 py-3 text-xs font-mono text-norea-text">{u.email}</td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${
                      u.role === "admin"
                        ? "bg-norea-primary text-white"
                        : "bg-norea-bg border border-norea-border text-norea-text"
                    }`}
                  >
                    {u.role}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {u.active === false ? (
                    <span className="text-xs text-status-erreurText">Désactivé</span>
                  ) : (
                    <span className="text-xs text-status-completText">Actif</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs tabular-nums text-norea-textMuted">{u.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {u.email !== me?.email && (
                      <>
                        <button
                          onClick={() => toggleActive(u.email, !u.active)}
                          className="p-1.5 text-norea-textMuted hover:text-norea-primary transition"
                          title={u.active === false ? "Activer" : "Désactiver"}
                          data-testid={`user-toggle-${u.email}`}
                        >
                          <Power className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setConfirmAction({ kind: "reset", email: u.email, name: u.name })}
                          className="p-1.5 text-norea-textMuted hover:text-norea-primary transition"
                          title="Réinitialiser le mot de passe"
                          data-testid={`user-reset-${u.email}`}
                        >
                          <KeyRound className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setConfirmAction({ kind: "delete", email: u.email, name: u.name })}
                          className="p-1.5 text-norea-textMuted hover:text-status-erreurText transition"
                          title="Supprimer"
                          data-testid={`user-delete-${u.email}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                    {u.email === me?.email && (
                      <span className="text-[10px] text-norea-textDisabled italic">vous</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <GoogleAllowlistCard />


      {openCreate && (
        <CreateUserModal
          onClose={() => setOpenCreate(false)}
          onCreated={(status) => {
            setOpenCreate(false);
            setMailStatus({ ...status, action: "create" });
            load();
          }}
        />
      )}
      {mailStatus && <MailStatusModal status={mailStatus} onClose={() => setMailStatus(null)} />}
      {confirmAction && (
        <ConfirmActionModal
          action={confirmAction}
          busy={busyEmail === confirmAction.email}
          onCancel={() => setConfirmAction(null)}
          onConfirm={() => (confirmAction.kind === "delete" ? doDelete(confirmAction.email) : doReset(confirmAction.email))}
        />
      )}
    </AppLayout>
  );
}

function CreateUserModal({ onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("agent");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!email.trim() || !name.trim()) return toast.error("Email et nom obligatoires");
    setBusy(true);
    try {
      const { data } = await api.post("/users", { email: email.trim(), name: name.trim(), role });
      toast.success("Compte créé");
      onCreated({ email: data.user.email, sent: data.email_sent, error: data.email_error });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={busy ? undefined : onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md border border-norea-border" onClick={(e) => e.stopPropagation()} data-testid="create-user-modal">
        <div className="px-6 py-4 border-b border-norea-border flex items-start justify-between">
          <h3 className="font-heading text-xl font-medium text-norea-text">Nouveau compte</h3>
          <button onClick={onClose} disabled={busy}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="overline block mb-1.5">Email (login)</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-field text-sm font-mono" placeholder="prenom.nom@norea.pro" data-testid="new-user-email" />
          </div>
          <div>
            <label className="overline block mb-1.5">Nom complet</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input-field text-sm" placeholder="Salomé Martin" data-testid="new-user-name" />
          </div>
          <div>
            <label className="overline block mb-1.5">Rôle</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="input-field text-sm" data-testid="new-user-role">
              <option value="agent">Agent</option>
              <option value="admin">Administrateur</option>
            </select>
          </div>
          <div className="text-[11px] text-norea-textMuted italic">
            Un mot de passe sera généré automatiquement et envoyé par email à l'utilisateur.
          </div>
        </div>
        <div className="px-6 py-4 bg-norea-bg border-t border-norea-border flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="btn-secondary">Annuler</button>
          <button onClick={submit} disabled={busy || !email || !name} className="btn-primary" data-testid="btn-create-user">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Créer
          </button>
        </div>
      </div>
    </div>
  );
}

function MailStatusModal({ status, onClose }) {
  const { email, sent, error, action } = status;
  const verbMail = action === "reset" ? "Nouveau mot de passe envoyé" : "Identifiants envoyés";
  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-6">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md border border-norea-border" data-testid="mail-status-modal">
        <div className="px-6 py-4 border-b border-norea-border">
          <h3 className="font-heading text-xl font-medium text-norea-text flex items-center gap-2">
            {sent ? (
              <>
                <Mail className="w-5 h-5 text-status-completText" />
                {verbMail}
              </>
            ) : (
              <>
                <MailWarning className="w-5 h-5 text-status-erreurText" />
                Échec de l'envoi
              </>
            )}
          </h3>
        </div>
        <div className="px-6 py-5">
          {sent ? (
            <>
              <div className="text-sm text-norea-text mb-3">
                Un email contenant les identifiants vient d'être envoyé à :
              </div>
              <div className="font-mono text-sm bg-norea-bg border border-norea-border rounded px-3 py-2 mb-3" data-testid="mail-status-recipient">
                {email}
              </div>
              <div className="text-[12px] text-norea-textMuted">
                L'utilisateur recevra son mot de passe et le lien vers la plateforme.
                Il pourra le changer dès sa première connexion.
              </div>
            </>
          ) : (
            <>
              <div className="bg-status-erreurBg border border-status-erreurBorder rounded-md px-3 py-2 mb-3 text-xs text-status-erreurText" data-testid="mail-status-error">
                {error || "Impossible d'envoyer l'email."}
              </div>
              <div className="text-[12px] text-norea-textMuted">
                Le compte a bien été {action === "reset" ? "réinitialisé" : "créé"}, mais l'envoi du mail a échoué.
                Vérifiez la configuration Gmail dans <strong>/connexions</strong>, puis utilisez « Réinitialiser » pour renvoyer le mot de passe.
              </div>
            </>
          )}
        </div>
        <div className="px-6 py-4 bg-norea-bg border-t border-norea-border flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-primary" data-testid="btn-close-mail-status">OK</button>
        </div>
      </div>
    </div>
  );
}


function ConfirmActionModal({ action, busy, onCancel, onConfirm }) {
  const isDelete = action.kind === "delete";
  const title = isDelete ? "Supprimer le compte ?" : "Réinitialiser le mot de passe ?";
  const desc = isDelete
    ? "Cette action est définitive. L'utilisateur ne pourra plus se connecter et son compte sera effacé."
    : "Un nouveau mot de passe sera généré et envoyé par email à l'utilisateur. L'ancien ne fonctionnera plus.";
  const cta = isDelete ? "Supprimer définitivement" : "Réinitialiser et envoyer";
  const ctaClass = isDelete
    ? "bg-status-erreurText hover:bg-status-erreurText/90 text-white"
    : "bg-norea-primary hover:bg-norea-primary/90 text-white";
  return (
    <div
      className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-6"
      onClick={busy ? undefined : onCancel}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md border border-norea-border"
        onClick={(e) => e.stopPropagation()}
        data-testid="confirm-action-modal"
      >
        <div className="px-6 py-4 border-b border-norea-border flex items-start justify-between">
          <h3 className="font-heading text-xl font-medium text-norea-text flex items-center gap-2">
            <AlertTriangle className={`w-5 h-5 ${isDelete ? "text-status-erreurText" : "text-status-aVerifierText"}`} />
            {title}
          </h3>
          <button onClick={onCancel} disabled={busy} className="text-norea-textMuted hover:text-norea-text">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-5">
          <div className="text-sm text-norea-text mb-3">{desc}</div>
          <div className="bg-norea-bg border border-norea-border rounded px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-norea-textMuted mb-0.5">Compte concerné</div>
            <div className="font-mono text-sm text-norea-text">{action.email}</div>
            {action.name && <div className="text-xs text-norea-textMuted">{action.name}</div>}
          </div>
        </div>
        <div className="px-6 py-4 bg-norea-bg border-t border-norea-border flex items-center justify-end gap-2">
          <button onClick={onCancel} disabled={busy} className="btn-secondary" data-testid="btn-cancel-confirm">
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition disabled:opacity-50 ${ctaClass}`}
            data-testid="btn-confirm-action"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : isDelete ? <Trash2 className="w-4 h-4" /> : <KeyRound className="w-4 h-4" />}
            {cta}
          </button>
        </div>
      </div>
    </div>
  );
}
